import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getAIChatSettings, updateAIChatSettings, testAIChatConnection } from "@/lib/admin/ai-chat.functions";
import { Loader2, Send, CheckCircle2, XCircle } from "lucide-react";

export const Route = createFileRoute("/_admin/admin/settings-ai-chat")({
  component: AIChatSettingsPage,
});

function AIChatSettingsPage() {
  const fetchSettings = useServerFn(getAIChatSettings);
  const updateSettings = useServerFn(updateAIChatSettings);
  const testConnection = useServerFn(testAIChatConnection);
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["admin", "ai-chat-settings"],
    queryFn: () => fetchSettings(),
  });

  const [form, setForm] = useState<any>(null);
  const [testMessage, setTestMessage] = useState("");
  const [testResult, setTestResult] = useState<{ success: boolean; response?: string; error?: string } | null>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        ...settings,
        api_key: "••••••••••••", // Mask the key
      });
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: (data: any) => updateSettings({ data }),
    onSuccess: () => {
      toast.success("AI Chat settings updated");
      queryClient.invalidateQueries({ queryKey: ["admin", "ai-chat-settings"] });
    },
    onError: () => {
      toast.error("Failed to update AI settings");
    },
  });

  const handleSave = () => {
    const patch = { ...form };
    if (patch.api_key === "••••••••••••") {
      delete patch.api_key;
    }
    mutation.mutate(patch);
  };

  const handleTest = async () => {
    if (!testMessage.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testConnection({ data: { message: testMessage } });
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, error: "Connection failed" });
    } finally {
      setTesting(false);
    }
  };

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader 
        title="AI Chat Settings" 
        description="Configure the AI model and provider for customer support chat."
      />

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Core Configuration</CardTitle>
              <CardDescription>Enable AI and select the model provider.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label>AI Chat Status</Label>
                  <p className="text-xs text-muted-foreground">Turn the AI assistant on or off.</p>
                </div>
                <Switch 
                  checked={form.enabled} 
                  onCheckedChange={(val) => setForm({ ...form, enabled: val })}
                />
              </div>

              <div className="space-y-2">
                <Label>Provider</Label>
                <select 
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}
                >
                  <option value="openrouter">OpenRouter</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Model ID</Label>
                <Input 
                  value={form.model_id}
                  onChange={(e) => setForm({ ...form, model_id: e.target.value })}
                  placeholder="openrouter/free"
                />
              </div>

              <div className="space-y-2">
                <Label>API Key</Label>
                <Input 
                  type="password"
                  value={form.api_key}
                  onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  placeholder="Enter your API key"
                />
                <p className="text-[10px] text-muted-foreground italic">Key is stored securely server-side.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Model Parameters</CardTitle>
              <CardDescription>Fine-tune the AI response behavior.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Temperature</Label>
                  <Input 
                    type="number" 
                    step="0.1" 
                    min="0" 
                    max="1" 
                    value={form.temperature}
                    onChange={(e) => setForm({ ...form, temperature: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Tokens</Label>
                  <Input 
                    type="number" 
                    value={form.max_tokens}
                    onChange={(e) => setForm({ ...form, max_tokens: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Persona & Messages</CardTitle>
              <CardDescription>Customize the assistant's voice and instructions.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>System Instructions</Label>
                <Textarea 
                  rows={8}
                  value={form.system_instructions}
                  onChange={(e) => setForm({ ...form, system_instructions: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Welcome Message</Label>
                <Input 
                  value={form.welcome_message}
                  onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Fallback Message</Label>
                <Input 
                  value={form.fallback_message}
                  onChange={(e) => setForm({ ...form, fallback_message: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Test Chat</CardTitle>
              <CardDescription>Test the current configuration.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input 
                  placeholder="Type a test message..." 
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTest()}
                />
                <Button onClick={handleTest} disabled={testing || !testMessage.trim()}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>

              {testResult && (
                <div className={`mt-4 rounded-lg border p-4 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.success ? (
                      <><CheckCircle2 className="h-4 w-4 text-green-600" /> <span className="text-sm font-bold text-green-600">SUCCESS</span></>
                    ) : (
                      <><XCircle className="h-4 w-4 text-red-600" /> <span className="text-sm font-bold text-red-600">FAILED</span></>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {testResult.response || testResult.error}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button variant="outline" onClick={() => setForm({ ...settings, api_key: "••••••••••••" })}>Reset</Button>
        <Button onClick={handleSave} disabled={mutation.isPending}>
          {mutation.isPending ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
